import { useEffect, useState } from "react";
import { useUser } from "../pages/auth/context/user-context";
import { getUser } from "../helpers/utils";

export const firstBookingCongratsStorageKey = (userId: string) =>
  `first_booking_congrats_dismissed_${userId}`;

/**
 * One-time "congrats on your first booking" popup gate.
 *
 * Opens on the user's first visit after the first-booking email was sent —
 * no expiry, it waits for them however long that takes. Show-once-then-gone:
 * dismissing (Got it / X / Esc / backdrop / Ask the assistant) writes a
 * per-user localStorage latch, so it never re-opens on this browser.
 */
export function useFirstBookingCongrats() {
  // UserContext is untyped (see main.tsx's `useUser() as any` idiom) —
  // narrow to the one field this hook reads.
  const { onboardingData } = useUser() as {
    onboardingData?: Record<string, unknown>;
  };
  const [open, setOpen] = useState(false);
  const userId = getUser()?.id as string | undefined;

  const sentAtRaw = onboardingData?.firstBookingEmailSentAt as
    | string
    | null
    | undefined;

  useEffect(() => {
    // Fully derived: also CLOSES an already-open popup if the user or
    // onboarding data changes to an ineligible state while mounted.
    const eligible =
      !!userId &&
      !!sentAtRaw &&
      !Number.isNaN(Date.parse(sentAtRaw)) &&
      !localStorage.getItem(firstBookingCongratsStorageKey(userId));
    setOpen(eligible);
  }, [userId, sentAtRaw]);

  const dismiss = () => {
    if (userId) {
      localStorage.setItem(
        firstBookingCongratsStorageKey(userId),
        new Date().toISOString(),
      );
    }
    setOpen(false);
  };

  return { open, dismiss, sentAt: sentAtRaw ?? null };
}
